import { getChatCompletion, ChatMessage } from "./api";
import { Scenario, scenarios, getDefaultScenario } from "./scenarios";

interface ChatServiceOptions {
  scenarioId?: string;
  scenario?: Scenario;
}

const BASE_NT2_PROMPT =
  `Je bent een vriendelijke Nederlandse gesprekspartner.
    Je oefent een realistische dialoog met een NT2-leerling (niveau A2).
    Je blijft altijd in de rol van een persoon in de situatie en noemt nooit dat je een AI, assistent of taalhulp bent.
    Je geeft korte, eenvoudige zinnen (max 10 woorden).
    Je antwoord is altijd kort: maximaal 2–3 zinnen.
    Je gebruikt alleen woorden die geschikt zijn voor niveau A2.
    Je houdt het gesprek op gang door een passende vervolgvraag te stellen.
    Je blijft volledig bij het onderwerp van het gesprek.
    Je stelt maximaal 1 vraag per keer aan de leerling.
    Als de leerling een fout maakt of iets onduidelijk zegt, interpreteer je dit vriendelijk en natuurlijk, zonder uitleg te geven over taal.
    Je geeft nooit meta-uitleg over oefenen, fouten of taal, maar reageert zoals een mens in de situatie.
    
    BELANGRIJK: Als het doel van het scenario is bereikt (zoals een afspraak maken, bestelling plaatsen, informatie uitwisselen), neem dan netjes afscheid in je antwoord en voeg dan op een nieuwe regel toe: STATE_FINISHED`;

class ChatService {
  private conversationHistory: ChatMessage[] = [];
  private activeScenario: Scenario;

  constructor(options: ChatServiceOptions = {}) {
    this.activeScenario = this.resolveScenario(options);
    this.conversationHistory = this.buildConversationSeed();
  }

  private resolveScenario(options: ChatServiceOptions): Scenario {
    if (options.scenario) {
      return options.scenario;
    }

    if (options.scenarioId && scenarios[options.scenarioId]) {
      return scenarios[options.scenarioId];
    }

    return getDefaultScenario();
  }

  private buildConversationSeed(): ChatMessage[] {
    return [
      {
        role: "system",
        content: `${BASE_NT2_PROMPT} Je helpt de gebruiker met het volgende scenario: ${this.activeScenario.aiInstructions}`,
      }
    ];
  }

  private resetConversationHistory(): void {
    this.conversationHistory = this.buildConversationSeed();
  }

  loadConversationHistory(
    turns: Array<{ role: "user" | "assistant"; content: string }>
  ): void {
    const history = turns.map((turn) => ({
      role: turn.role,
      content: turn.content,
    }));
    this.conversationHistory = [...this.buildConversationSeed(), ...history];
  }

  setScenario(scenarioOrId: Scenario | string): void {
    const nextScenario =
      typeof scenarioOrId === "string" ? scenarios[scenarioOrId] : scenarioOrId;

    if (!nextScenario || nextScenario.id === this.activeScenario.id) {
      return;
    }

    this.activeScenario = nextScenario;
    this.resetConversationHistory();
  }

  getScenario(): Scenario {
    return this.activeScenario;
  }

  async getChatResponse(userMessage: string): Promise<string> {
    try {
      // Add user message to conversation history
      this.conversationHistory.push({
        role: "user",
        content: userMessage,
      });

      // Get response from backend API
      const response = await getChatCompletion(this.conversationHistory);

      // Check if conversation is finished (STATE_FINISHED at end of message)
      if (response.content.includes("STATE_FINISHED")) {
        console.log("Gesprek is afgerond door AI");
        
        // Extract the actual message without STATE_FINISHED
        const actualMessage = response.content.replace(/\s*STATE_FINISHED\s*$/, "").trim();
        
        if (actualMessage) {
          // Add the farewell message to conversation history
          this.conversationHistory.push({
            role: "assistant",
            content: actualMessage,
          });
          
          console.log("Chat API farewell response:", actualMessage);
          return `${actualMessage}\n\nSTATE_FINISHED`;
        } else {
          return "STATE_FINISHED";
        }
      }

      // Add assistant response to conversation history
      this.conversationHistory.push({
        role: "assistant",
        content: response.content,
      });

      // Keep conversation history manageable (last 10 exchanges)
      if (this.conversationHistory.length > 22) {
        // 2 system + 10 pairs
        const systemMessages = this.conversationHistory.filter(
          (message) => message.role === "system"
        );
        const recentMessages = this.conversationHistory
          .filter((message) => message.role !== "system")
          .slice(-20); // keep last 20 user/assistant messages

        this.conversationHistory = [...systemMessages, ...recentMessages];
      }

      console.log("Chat API response:", response.content);
      return response.content;
    } catch (error) {
      console.error("Fout bij Chat API call:", error);
      throw new Error(`Chat API fout: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  clearConversation(): void {
    this.resetConversationHistory();
    console.log("Conversatie geschiedenis gewist");
  }
}

export default ChatService;
